import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LockOutlined, LogoutOutlined, ReloadOutlined } from "@ant-design/icons";
import { Flex, Tag } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Card } from "@av/components/atoms/Card";
import { Image } from "@av/components/atoms/Image";
import { Typography } from "@av/components/atoms/typography/Typography";
import { useDocumentTitle } from "@av/components/templates/layouts/errors/hooks/useDocumentTitle";
import { GenericPlatformLayout } from "../components/GenericPlatformLayout";

const useStyles = createStyles(({ token, css }) => ({
  page: css`
    padding: ${token.paddingXL}px ${token.paddingLG}px;
  `,

  card: css`
    width: min(860px, 100%);
    border-radius: ${token.borderRadiusLG}px;
    box-shadow: ${token.boxShadowTertiary};
    border: 1px solid ${token.colorBorderSecondary};
    overflow: hidden;
  `,

  hero: css`
    background: linear-gradient(135deg, ${token.colorBgContainer}, ${token.colorPrimaryBg});
    padding: ${token.paddingLG}px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,

  image: css`
    max-width: 320px;
    width: 100%;
  `,

  content: css`
    padding: ${token.paddingXL}px;
  `,

  title: css`
    margin-top: ${token.marginXS}px;
    margin-bottom: ${token.marginXS}px;
  `,

  hintCard: css`
    background: ${token.colorBgContainer};
    border-radius: ${token.borderRadiusLG}px;
    padding: ${token.paddingMD}px;
    border: 1px dashed ${token.colorBorderSecondary};
  `,

  hintList: css`
    margin: ${token.marginXS}px 0 0;
    padding-left: ${token.paddingMD}px;
    color: ${token.colorTextSecondary};
  `,

  actions: css`
    margin-top: ${token.marginLG}px;
  `,
}));

export function NoAppAccessErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { styles } = useStyles();

  useDocumentTitle(t("common.components.errors_noAccess_title"));

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleSignOut = () => {
    navigate("/login");
  };

  return (
    <GenericPlatformLayout>
      <Flex justify="center" className={styles.page}>
        <Card
          testIdPrefix="no-access-error-card"
          className={styles.card}
          bodyStyle={{ padding: 0 }}
        >
          <Flex vertical>
            <div className={styles.hero}>
              <Image
                testIdPrefix="no-app-access"
                src="/errors/403_forbidden.png"
                alt={t("common.components.errors_noAccess_alt")}
                preview={false}
                className={styles.image}
              />
            </div>
            <div className={styles.content}>
              <Tag icon={<LockOutlined />} color="gold" data-testid="no-app-access-tag">
                {t("common.components.errors_noAccess_tag")}
              </Tag>
              <Typography.Title testIdPrefix="no-app-access" level={1} className={styles.title}>
                {t("common.components.errors_noAccess_title")}
              </Typography.Title>
              <Typography.Paragraph testIdPrefix="no-app-access-message" type="secondary">
                {t("common.components.errors_noAccess_message")}
              </Typography.Paragraph>
              <div className={styles.hintCard}>
                <Typography.Text testIdPrefix="no-app-access-help-title" strong>
                  {t("common.components.errors_noAccess_helpTitle")}
                </Typography.Text>
                <ul className={styles.hintList} data-testid="no-app-access-help-list">
                  <li data-testid="no-app-access-help-admin">
                    {t("common.components.errors_noAccess_helpItemAdmin")}
                  </li>
                  <li data-testid="no-app-access-help-account">
                    {t("common.components.errors_noAccess_helpItemAccount")}
                  </li>
                  <li data-testid="no-app-access-help-wait">
                    {t("common.components.errors_noAccess_helpItemWait")}
                  </li>
                </ul>
              </div>
              <Flex className={styles.actions} gap="middle" wrap="wrap">
                <Button
                  testIdPrefix="no-app-access-refresh"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                >
                  {t("common.components.errors_noAccess_actionRefresh")}
                </Button>
                <Button
                  testIdPrefix="no-app-access-signout"
                  type="primary"
                  icon={<LogoutOutlined />}
                  onClick={handleSignOut}
                >
                  {t("common.components.errors_noAccess_actionSignOut")}
                </Button>
              </Flex>
            </div>
          </Flex>
        </Card>
      </Flex>
    </GenericPlatformLayout>
  );
}
